import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../services/config';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Avatar,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemAvatar,
  Checkbox,
  FormControlLabel,
  Alert,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Download,
  Print,
  Person,
  CalendarToday,
  LocationOn,
  Schedule,
  Group,
  Assignment,
  CheckCircle,
  Warning,
  Info,
  School,
  Security,
  Build,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';

interface Attendee {
  _id: string;
  firstName: string;
  lastName: string;
  position: string;
  company: string;
  signature?: string;
  attended: boolean;
  avatar?: string;
}

interface SafetyTopic {
  title: string;
  description: string;
  keyPoints: string[];
  hazards: string[];
  preventiveMeasures: string[];
}

interface ToolboxTalkDetail {
  _id: string;
  project: {
    name: string;
    location: string;
  };
  date: string;
  time: string;
  duration: number; // in minutes
  location: string;
  conductor: {
    firstName: string;
    lastName: string;
    position: string;
    certification?: string;
    avatar?: string;
  };
  topic: string;
  category: 'safety' | 'quality' | 'environmental' | 'general' | 'emergency';
  safetyTopics: SafetyTopic[];
  objectives: string[];
  attendees: Attendee[];
  totalAttendees: number;
  weatherConditions?: string;
  equipmentDiscussed: string[];
  hazardsIdentified: string[];
  actionItems: {
    item: string;
    assignedTo: string;
    dueDate: string;
    priority: 'low' | 'medium' | 'high';
    status: 'pending' | 'in-progress' | 'completed';
  }[];
  followUpRequired: boolean;
  followUpDate?: string;
  notes: string;
  attachments: string[];
  status: 'scheduled' | 'completed' | 'cancelled';
  createdAt: string;
  updatedAt: string;
}

const ToolboxTalkDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [talk, setTalk] = useState<ToolboxTalkDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const getPrimaryTopic = (t: any): string => {
      if (typeof t?.topic === 'string') return t.topic;
      if (t?.topic?.primary) return t.topic.primary;
      return typeof t?.title === 'string' ? t.title : 'Toolbox Talk';
    };

    const normalizeText = (val: any): string => {
      if (val == null) return '';
      if (typeof val === 'string') return val;
      if (typeof val === 'number') return String(val);
      // If it's an object, attempt to extract a reasonable string
      const candidates = [val.description, val.text, val.value, val.name, val.title];
      for (const c of candidates) {
        if (typeof c === 'string') return c;
        if (typeof c === 'number') return String(c);
        if (c && typeof c === 'object') {
          // One more level deep
          const deep = [c.description, c.text, c.value, c.name, c.title].find((d) => typeof d === 'string');
          if (typeof deep === 'string') return deep;
        }
      }
      return '';
    };

    const normalizeList = (arr: any, picks?: string[]): string[] => {
      if (!Array.isArray(arr)) return [];
      return arr.map((item) => {
        if (typeof item === 'string') return item;
        if (typeof item === 'number') return String(item);
        const fields = picks || ['point', 'title', 'name', 'text', 'description', 'value'];
        for (const f of fields) {
          if (typeof item?.[f] === 'string') return item[f];
        }
        return '';
      }).filter(Boolean);
    };

    const normalizeCategory = (c: any): ToolboxTalkDetail['category'] => {
      const v = typeof c === 'string' ? c : (c?.name || c?.title || 'general');
      return (['safety','quality','environmental','general','emergency'].includes(v) ? v : 'general') as ToolboxTalkDetail['category'];
    };

    const mapTalk = (t: any): ToolboxTalkDetail => ({
      _id: t._id || t.id || id || '',
      project: {
        name: normalizeText(t.project?.name || t.projectName || 'Unassigned'),
        location: normalizeText(t.project?.location || t.location || ''),
      },
      date: (t.date || t.scheduledDate || new Date().toISOString().split('T')[0]).toString().slice(0, 10),
      time: t.time || (t.dateTime ? new Date(t.dateTime).toTimeString().slice(0,5) : '09:00'),
      duration: Number(t.duration || 0),
      location: normalizeText(t.location || t.project?.location || ''),
      conductor: {
        firstName: normalizeText(t.conductor?.firstName || t.conductorFirstName || t.conductor?.name?.split(' ')?.[0] || 'Unknown'),
        lastName: normalizeText(t.conductor?.lastName || t.conductorLastName || t.conductor?.name?.split(' ')?.[1] || ''),
        position: normalizeText(t.conductor?.position || t.conductorPosition || ''),
        certification: t.conductor?.certification,
        avatar: t.conductor?.avatar,
      },
      topic: getPrimaryTopic(t),
      category: normalizeCategory(t.category),
      safetyTopics: Array.isArray(t.topics)
        ? t.topics.map((x: any) => ({
            title: normalizeText(x.title) || '',
            description: normalizeText(x.description) || '',
            keyPoints: normalizeList(x.keyPoints, ['point','text','description','title']),
            hazards: normalizeList(x.hazards, ['title','name','text','description']),
            preventiveMeasures: normalizeList(x.preventiveMeasures, ['title','name','text','description']),
          }))
        : [],
      objectives: normalizeList(t.objectives, ['title','text','description','objective']),
      attendees: (t.attendees || []).map((a: any) => ({
        _id: a._id || a.id || `${normalizeText(a.firstName || a.name?.split(' ')?.[0] || '')}-${normalizeText(a.lastName || a.name?.split(' ')?.[1] || '')}`,
        firstName: normalizeText(a.firstName || a.name?.split(' ')?.[0] || 'Unknown'),
        lastName: normalizeText(a.lastName || a.name?.split(' ')?.[1] || ''),
        position: normalizeText(a.position || a.role || ''),
        company: normalizeText(a.company || a.org || ''),
        signature: a.signature,
        attended: Boolean(a.present ?? a.attended ?? true),
        avatar: a.avatar,
      })),
      totalAttendees: Array.isArray(t.attendees) ? t.attendees.length : Number(t.attendeesCount || 0),
      weatherConditions: normalizeText(t.weatherConditions) || undefined,
      equipmentDiscussed: normalizeList(t.equipmentDiscussed, ['name','title','text','description']),
      hazardsIdentified: normalizeList(t.hazardsIdentified, ['name','title','text','description']),
      actionItems: (t.actionItems || []).map((ai: any) => ({
        item: normalizeText(ai.item) || normalizeText(ai.title) || '',
        assignedTo: normalizeText(ai.assignedTo) || normalizeText(ai.assignee) || '',
        dueDate: ai.dueDate || ai.due || '',
        priority: (ai.priority || 'medium') as 'low' | 'medium' | 'high',
        status: (ai.status || 'pending') as 'pending' | 'in-progress' | 'completed',
      })),
      followUpRequired: Boolean(t.followUpRequired || false),
      followUpDate: t.followUpDate || undefined,
      notes: normalizeText(t.notes),
      attachments: normalizeList(t.attachments, ['name','title','fileName','url','path','description']),
      status: (t.status || 'scheduled') as ToolboxTalkDetail['status'],
      createdAt: t.createdAt || new Date().toISOString(),
      updatedAt: t.updatedAt || new Date().toISOString(),
    });

    const fetchTalk = async () => {
      setLoading(true);
      setError(null);
      try {
        if (!id) throw new Error('Invalid talk ID');
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_BASE_URL}/toolbox-talks/${id}` , {
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
          },
        });
        if (!res.ok) throw new Error('Failed to fetch toolbox talk');
        const data = await res.json();
        const item = data?.data ?? data;
        setTalk(mapTalk(item));
      } catch (e: any) {
        console.error('Error fetching toolbox talk detail:', e);
        setError(e?.message || 'Error fetching toolbox talk');
        setTalk(null);
      } finally {
        setLoading(false);
      }
    };

    fetchTalk();
  }, [id]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'scheduled':
        return 'info';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'safety':
        return 'error';
      case 'quality':
        return 'warning';
      case 'environmental':
        return 'success';
      case 'emergency':
        return 'error';
      case 'general':
        return 'info';
      default:
        return 'default';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'safety':
        return <Security />;
      case 'quality':
        return <Assignment />;
      case 'environmental':
        return <Info />;
      case 'emergency':
        return <Warning />;
      case 'general':
        return <School />;
      default:
        return <Assignment />;
    }
  };

  const getActionStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in-progress':
        return 'warning';
      case 'pending':
        return 'default';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatDateTime = (date: string, time: string) => {
    const dateTime = new Date(`${date}T${time}:00`);
    return dateTime.toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Typography>Loading toolbox talk...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">{error}</Typography>
      </Box>
    );
  }

  if (!talk) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">Toolbox talk not found</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/toolbox-talks')} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" fontWeight="bold" sx={{ flex: 1 }}>
          Toolbox Talk Details
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<Edit />}>
            Edit
          </Button>
          <Button variant="outlined" startIcon={<Download />}>
            Export
          </Button>
          <Button variant="outlined" startIcon={<Print />}>
            Print
          </Button>
        </Box>
      </Box>

      {/* Talk Info Card */}
      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Avatar
              src={talk.conductor.avatar}
              sx={{ width: 60, height: 60, mr: 2 }}
            >
              {talk.conductor.firstName[0]}{talk.conductor.lastName[0]}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" fontWeight="bold">
                {talk.topic}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Conducted by: {talk.conductor.firstName} {talk.conductor.lastName} ({talk.conductor.position})
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {talk.project.name} - {talk.project.location}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip
                icon={getCategoryIcon(talk.category)}
                label={talk.category.toUpperCase()}
                color={getCategoryColor(talk.category) as any}
                sx={{ mr: 1 }}
              />
              <Chip
                label={talk.status.toUpperCase()}
                color={getStatusColor(talk.status) as any}
              />
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarToday fontSize="small" color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary">Date & Time</Typography>
                <Typography variant="body2" fontWeight="medium">
                  {formatDateTime(talk.date, talk.time)}
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Schedule fontSize="small" color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary">Duration</Typography>
                <Typography variant="body2" fontWeight="medium">
                  {talk.duration} minutes
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationOn fontSize="small" color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary">Location</Typography>
                <Typography variant="body2" fontWeight="medium">
                  {talk.location}
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Group fontSize="small" color="action" />
              <Box>
                <Typography variant="caption" color="text.secondary">Attendance</Typography>
                <Typography variant="body2" fontWeight="medium">
                  {talk.totalAttendees} / {talk.attendees.length} attendees
                </Typography>
              </Box>
            </Box>
          </Box>

          {talk.weatherConditions && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">Weather Conditions</Typography>
              <Typography variant="body2" fontWeight="medium">
                {talk.weatherConditions}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 3 }}>
        {/* Left Column */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Objectives */}
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Learning Objectives
              </Typography>
              
              <List dense>
                {talk.objectives.map((objective, index) => (
                  <ListItem key={index} sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircle color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={objective}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>

          {/* Safety Topics */}
          {talk.safetyTopics.map((topic, index) => (
            <Card key={index} sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                  {topic.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {topic.description}
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Key Points
                  </Typography>
                  <List dense>
                    {topic.keyPoints.map((point, pointIndex) => (
                      <ListItem key={pointIndex} sx={{ py: 0.25 }}>
                        <ListItemIcon sx={{ minWidth: 24 }}>
                          <CheckCircle color="success" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={point}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Identified Hazards
                  </Typography>
                  <List dense>
                    {topic.hazards.map((hazard, hazardIndex) => (
                      <ListItem key={hazardIndex} sx={{ py: 0.25 }}>
                        <ListItemIcon sx={{ minWidth: 24 }}>
                          <Warning color="warning" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={hazard}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>

                <Box>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Preventive Measures
                  </Typography>
                  <List dense>
                    {topic.preventiveMeasures.map((measure, measureIndex) => (
                      <ListItem key={measureIndex} sx={{ py: 0.25 }}>
                        <ListItemIcon sx={{ minWidth: 24 }}>
                          <Security color="primary" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={measure}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </CardContent>
            </Card>
          ))}

          {/* Action Items */}
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Action Items
              </Typography>
              
              {talk.actionItems.map((item, index) => (
                <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Chip
                      label={item.status.replace('-', ' ').toUpperCase()}
                      color={getActionStatusColor(item.status) as any}
                      size="small"
                    />
                    <Chip
                      label={item.priority.toUpperCase()}
                      color={getPriorityColor(item.priority) as any}
                      size="small"
                    />
                  </Box>
                  <Typography variant="body2" fontWeight="medium" gutterBottom>
                    {item.item}
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      <strong>Assigned To:</strong> {item.assignedTo}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      <strong>Due Date:</strong> {new Date(item.dueDate).toLocaleDateString()}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Box>

        {/* Right Column */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Attendees */}
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Attendees ({talk.totalAttendees}/{talk.attendees.length})
              </Typography>
              
              <List dense>
                {talk.attendees.map((attendee) => (
                  <ListItem key={attendee._id} sx={{ py: 1 }}>
                    <ListItemAvatar>
                      <Avatar src={attendee.avatar} sx={{ width: 32, height: 32 }}>
                        {attendee.firstName[0]}{attendee.lastName[0]}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={`${attendee.firstName} ${attendee.lastName}`}
                      secondary={`${attendee.position} - ${attendee.company}`}
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={attendee.attended}
                          disabled
                          size="small"
                        />
                      }
                      label=""
                      sx={{ ml: 1 }}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>

          {/* Equipment Discussed */}
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Equipment Discussed
              </Typography>
              
              <List dense>
                {talk.equipmentDiscussed.map((equipment, index) => (
                  <ListItem key={index} sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Build color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={equipment}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>

          {/* Hazards Identified */}
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Site Hazards Identified
              </Typography>
              
              <List dense>
                {talk.hazardsIdentified.map((hazard, index) => (
                  <ListItem key={index} sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Warning color="warning" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={hazard}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>

          {/* Follow-up Information */}
          {talk.followUpRequired && (
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                  Follow-up Required
                </Typography>
                
                <Alert severity="info" sx={{ mb: 2 }}>
                  Follow-up session scheduled
                </Alert>
                
                <Box>
                  <Typography variant="caption" color="text.secondary">Follow-up Date</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {talk.followUpDate ? new Date(talk.followUpDate).toLocaleDateString() : 'TBD'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Attachments */}
          {talk.attachments.length > 0 && (
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                  Attachments
                </Typography>
                
                <List dense>
                  {talk.attachments.map((attachment, index) => (
                    <ListItem key={index} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <Assignment color="primary" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={attachment}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          )}
        </Box>
      </Box>

      {/* Notes */}
      {talk.notes && (
        <Card sx={{ mt: 3, borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              Conductor Notes
            </Typography>
            <Typography variant="body2" sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              {talk.notes}
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default ToolboxTalkDetail;